import { useMutation, useQuery } from '@tanstack/react-query';
import type { EmailSendCategory } from '@erp/shared-types';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';

/**
 * Send a composed reply through the org's Microsoft Graph (Azure) mailbox.
 * The caller handles success/error toasts so it controls the panel UX.
 */
export function useSendComposeReply() {
  return useMutation(orpc.composeReply.send.mutationOptions());
}

/**
 * Mailboxes the current user may send from + the org's default send UPN for
 * the given category (server defaults to "general" when omitted).
 */
export function useComposeReplySendableMailboxes(category?: EmailSendCategory) {
  return useQuery(
    orpc.composeReply.sendableMailboxes.queryOptions({ input: { category } }),
  );
}

/** The org's configured default sending mailboxes, one per category. */
export function useDefaultSendMailbox() {
  return useQuery(
    orpc.composeReply.defaultSend.get.queryOptions({ input: undefined }),
  );
}

export function useSetDefaultSendMailbox() {
  const invalidate = useInvalidateRpcQueryFn((o) => [
    o.composeReply.defaultSend.get.key(),
    o.composeReply.sendableMailboxes.key(),
  ]);
  return useMutation({
    mutationFn: (input: {
      category: EmailSendCategory;
      emailIngestMailboxId: string | null;
    }) => rpcClient.composeReply.defaultSend.set(input),
    onSuccess: () => invalidate({}),
  });
}
